import React, { useState, useEffect } from "react";
// import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // Use `useHistory` for react-router-dom v5
import SearchIcon from "@mui/icons-material/Search";
import { styled, alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import InputBase from "@mui/material/InputBase";
import { Autocomplete, createFilterOptions, Popper } from "@mui/material";
import { Paper } from "@mui/material";
import { fetchUSStockList } from "../api/usStockList";
import Skeleton from "@mui/material/Skeleton";
// import { list } from "../api/stockList";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";

// Styled components
const Search = styled("div")(({ theme }) => ({
	position: "relative",
	borderRadius: 25,
	// borderRadius: theme.shape.borderRadius,
	backgroundColor: alpha("#3a2206", 0.05),
	// "&:hover": {
	// 	backgroundColor: alpha(theme.palette.common.white, 0.25),
	// },
	marginRight: theme.spacing(2),
	marginLeft: 0,
	width: "100%",
	// before it was 'sm'
	[theme.breakpoints.up("md")]: {
		marginLeft: theme.spacing(3),
		width: "auto",
	},
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
	padding: theme.spacing(0, 2),
	height: "100%",
	position: "absolute",
	pointerEvents: "none",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
	color: "inherit",
	"& .MuiInputBase-input": {
		padding: theme.spacing(1, 1, 1, 0),
		// vertical padding + font size from searchIcon
		paddingLeft: `calc(1em + ${theme.spacing(4)})`,
		paddingRight: `calc(1em + ${theme.spacing(3)})`,
		transition: theme.transitions.create("width"),
		width: "100%",
		[theme.breakpoints.up("md")]: {
			width: `calc(30vw - 0.5em - ${theme.spacing(3)})`,
			// width: '40ch',
		},
	},
}));

const StyledSearchSkeleton = styled(Skeleton)(({ theme }) => ({
	padding: theme.spacing(1, 1, 1, 0),
	// vertical padding + font size from searchIcon
	paddingLeft: `calc(1em + ${theme.spacing(4)})`,
	transition: theme.transitions.create("width"),
	width: "auto",
	height: theme.spacing(3), // Match height roughly to the input text size and padding
	borderRadius: 25, // Mimic input rounding if any
	[theme.breakpoints.up("md")]: {
		width: "30vw", // Same width as StyledInputBase for medium and up screens
	},
}));

export default function SearchStock() {
	const theme = useTheme();
	const navigate = useNavigate(); // Initialize navigate hook
	const isMidScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [usStockList, setUsStockList] = useState([]);
	const [inputValue, setInputValue] = useState(""); // Define the state for the input value

	// This effect runs only once, when the component is first mounted
	useEffect(() => {
		// Fetch the stock data only once when the component mounts
		(async function getUsStockList() {
			try {
				const data = await fetchUSStockList();
				setUsStockList(data); // Store the data in state
			} catch (error) {
				console.error("Error fetching stock list:", error);
			}
		})();
	}, []); // Empty dependency array ensures this runs only once

	if (!usStockList.length) {
		return (
			<Search>
				<SearchIconWrapper>
					<SearchIcon />
				</SearchIconWrapper>
				<StyledSearchSkeleton variant="rectangular" />
			</Search>
		);
	}

	const filterOptions = createFilterOptions({
		limit: 10,
	});

	return (
		<Search>
			<SearchIconWrapper>
				<SearchIcon sx={{ color: "#3a2206", opacity: 0.6 }} />
			</SearchIconWrapper>
			<Autocomplete
				disablePortal
				blurOnSelect
				autoHighlight
				clearOnEscape={true}
				clearIcon={<ClearIcon sx={{ fontSize: "1rem" }} />}
				clearText=""
				sx={{
					"& .MuiAutocomplete-clearIndicator": {
						visibility:
							inputValue.length > 0 ? "visible" : "hidden",
						padding: 0,
						// color: "#fff",
						height: "1.5rem",
						width: "1.5rem",
						m: 1.5,
						"&:hover": {
							bgcolor: "transparent",
						},
					},
				}}
				id="stock-search"
				freeSolo
				filterOptions={filterOptions}
				options={inputValue.length > 0 ? usStockList : []}
				// options={inputValue.length > 0 ? list : []}
				inputValue={inputValue}
				onInputChange={(event, newInputValue) => {
					setInputValue(newInputValue); // Track the input value
				}}
				getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
				onChange={(event, selectedOption) => {
					if (selectedOption) {
						navigate(
							`/stock-analyzer/${selectedOption.symbol}/key-indicators`
						);
						setTimeout(() => setInputValue(""), 0); // Delay clearing to avoid race conditions
					}
				}}
				PaperComponent={(props) => {
					const { key, ...otherProps } = props;
					return (
						<Paper
							{...otherProps}
							key={key} // Pass `key` separately
							elevation={2}
							display="flex"
							sx={{
								mt: 0.5,
								borderRadius: 3,
								pt: 1,
								pb: 1,
								pr: 0.5,
								pl: 0.5,
								"& .MuiAutocomplete-listbox": {
									pt: 0, // Remove padding from listbox
									pb: 0, // Remove padding from listbox
									"@media (pointer: fine)": {
										"&::-webkit-scrollbar": {
											width: "5px",
											height: "5px",
										},
										"&::-webkit-scrollbar-thumb": {
											background: "#ccc",
											borderRadius: "10px",
											transition: "background 0.3s ease",
										},
										"&::-webkit-scrollbar-thumb:hover": {
											background: "#b0b0b0",
										},
									},
								},
							}}
						/>
					);
				}}
				PopperComponent={(props) => {
					const { key, ...otherProps } = props;
					return (
						<Popper
							{...otherProps}
							key={key} // Pass `key` separately
							sx={{
								minWidth: isMobile ? "80vw" : "",
							}}
						/>
					);
				}}
				renderOption={(props, option) => {
					const { key, ...otherProps } = props;

					return (
						<ListItem
							{...otherProps}
							key={key} // Pass `key` separately
							sx={{
								width: "auto",
								ml: 0.5,
								mr: 0.5,
								borderRadius: 2,
							}}
						>
							<Box
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								sx={{
									minWidth: "100%",
								}}
							>
								<Box display="flex" flexDirection="column">
									<Typography
										variant="subtitle1"
										component="div"
										fontWeight={"bold"}
									>
										{option.symbol}
									</Typography>
									<Typography
										variant="body1"
										color="textPrimary"
									>
										{isMobile
											? option.name &&
											  option.name.length > 30
												? `${option.name.slice(
														0,
														30
												  )}...`
												: option.name
											: option.name &&
											  option.name.length > 40
											? `${option.name.slice(0, 40)}...`
											: option.name}
									</Typography>
								</Box>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="flex-end"
									sx={{ ml: isMidScreen ? "0.5rem" : "2rem" }}
								>
									<Typography
										variant="subtitle1"
										component="div"
										fontWeight="bold"
									>
										{option.price.toFixed(2)}
									</Typography>
									<Typography
										variant="subtitle2"
										component="div"
										color="textSecondary"
									>
										{option.exchangeShortName}
									</Typography>
								</Box>
							</Box>
						</ListItem>
					);
				}}
				renderInput={(params) => (
					<StyledInputBase
						ref={params.InputProps.ref}
						placeholder="Stock Search"
						inputProps={{
							...params.inputProps,
							"aria-label": "search",
						}}
						endAdornment={
							inputValue.length > 0
								? params.InputProps.endAdornment
								: null
						}
						sx={{ width: "100%" }}
					/>
				)}
			/>
		</Search>
	);
}
